import type { ReactNode } from 'react';
import { Button, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon, type IconName } from '@launchpad-ui/icons';

import './styles.scss';

export type CircularIconButtonIntent = 'success' | 'danger' | 'warning' | 'primary' | 'neutral' | 'purple' | 'info';
export type CircularIconButtonSize = 'small' | 'medium' | 'large';

type CircularIconButtonProps = {
  /** The name of the icon to display */
  iconName: IconName;
  /** The visual intent/color scheme */
  intent?: CircularIconButtonIntent;
  /** The size of the button */
  size?: CircularIconButtonSize;
  /** Tooltip content to show on hover (can be text or React components) */
  tooltip?: ReactNode;
  /** Tooltip placement */
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  /** Accessible label for screen readers */
  ariaLabel?: string;
  /** Click handler */
  onPress?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
};

const getIntentClass = (intent: CircularIconButtonIntent) => `circular-icon-button--${intent}`;
const getSizeClass = (size: CircularIconButtonSize) => `circular-icon-button--${size}`;

export function CircularIconButton({
  iconName,
  intent = 'neutral',
  size = 'medium',
  tooltip,
  tooltipPlacement = 'top',
  ariaLabel,
  onPress,
  className = '',
  disabled = false,
}: CircularIconButtonProps) {
  const buttonClasses = ['circular-icon-button', getSizeClass(size), getIntentClass(intent), className]
    .filter(Boolean)
    .join(' ');

  const button = (
    <Button
      className={buttonClasses}
      variant="minimal"
      size="small"
      aria-label={ariaLabel || (typeof tooltip === 'string' ? tooltip : 'Information')}
      onPress={onPress}
      isDisabled={disabled}
    >
      <Icon name={iconName} />
    </Button>
  );

  // If tooltip is provided, wrap with TooltipTrigger
  if (tooltip) {
    return (
      <span className="status-icon-container">
        <TooltipTrigger>
          {button}
          <Tooltip placement={tooltipPlacement} variant="popover">
            {tooltip}
          </Tooltip>
        </TooltipTrigger>
      </span>
    );
  }

  return <span className="status-icon-container">{button}</span>;
}
